import React, { useState } from "react";
import { useImmer } from "use-immer";
import ChatInput from "./chat-input";
import ChatMessages from "./chat-messages";
import { parseSSEStream } from "../../utils/parse-sse-stream";
import { sendChatMessage, createNewChat } from "./api";
import useAutoScroll from "../../hooks/use-auto-scroll";
import ChatCharts from "./chat-charts";
import vdcom from "../../lib/vd";
import ChatHistory from "./chat-history";
import page from "page";
import { CHAT_SUGGESTIONS } from "./constants";
import logo from "assets/images/logo_color.png";

import "./styles.scss";
import ChatSuggestion from "./chat-suggestion";

function Chatbot({
  title = "Visual Deck Chatbot",
  subtitle = "Powered by AI",
  initialChat = undefined,
}) {
  const [chatId, setChatId] = useState(initialChat?.id);
  const [messages, setMessages] = useImmer(initialChat?.messageHistory || []);
  const [chats, setChats] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [queries, setQueries] = useState(initialChat?.queryHistory || []);
  const [loadingChatHistory, setLoadingChatHistory] = useState(false);
  const isLoading = messages.length && messages[messages.length - 1].loading;
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    loadChatHistory();
  }, []);

  React.useEffect(() => {
    if (initialChat) {
      setMessages(() => initialChat.messageHistory);
      setChatId(initialChat.id);
      setQueries(initialChat.queryHistory ?? []);
    } else {
      setMessages(() => []);
      setChatId(null);
      setQueries([]);
    }
  }, [initialChat]);

  const loadChatHistory = async () => {
    setLoadingChatHistory(true);
    vdcom
      .ai()
      .recentChats()
      .then((response) => {
        setChats(response);
      })
      .catch((err) => console.log)
      .finally(() => {
        setLoadingChatHistory(false);
      });
  };

  useAutoScroll(scrollRef, isLoading);

  const clearState = () => {
    setMessages(() => []);
    setChatId(null);
    setQueries([]);
    loadChatHistory();
    page("/chat");
  };

  const handleOnDeleteChat = async (chatId) => {
    try {
      await vdcom.ai().deleteChat(chatId);
      loadChatHistory();

      if (chatId === chatId) {
        clearState();
      }
    } catch (err) {
      console.log(err);
    }
  };

  async function submitNewMessage() {
    const trimmedMessage = newMessage.trim();
    if (trimmedMessage === "") return;

    setMessages((draft) => [
      ...draft,
      { role: "user", message: trimmedMessage },
      { role: "assistant", message: "", sources: [], loading: true },
    ]);
    setNewMessage("");

    let chatIdOrNew = chatId;

    try {
      if (!chatId) {
        const { id } = await createNewChat();
        setChatId(id);
        chatIdOrNew = id;
      }

      const stream = await sendChatMessage(chatIdOrNew, trimmedMessage);

      for await (const chunk of parseSSEStream(stream)) {
        console.log(chunk);
        if (chunk.type === "message") {
          setMessages((draft) => {
            draft[draft.length - 1].message += chunk.data;
          });
        } else if (chunk.type === "query") {
          setQueries((draft) => [...draft, JSON.parse(chunk.data)]);
        }
      }

      setMessages((draft) => {
        draft[draft.length - 1].loading = false;
      });
    } catch (err) {
      console.log(err);
      setMessages((draft) => {
        draft[draft.length - 1].loading = false;
        draft[draft.length - 1].error = true;
      });
    }
  }

  return (
    <div className="overflow-hidden grid grid-cols-[1fr_3fr_2fr] h-full">
      <div className=" p-4 overflow-y-auto ">
        <ChatHistory
          chats={chats}
          isLoading={loadingChatHistory}
          activeChatId={chatId}
          deleteChat={handleOnDeleteChat}
          clearState={clearState}
        />
      </div>
      <div
        className={`flex flex-col h-full overflow-y-scroll relative min-h-0 p-4
          ${messages.length >= 1 || isLoading ? "" : "justify-center"}
          `}
        style={{
          borderLeft: "1px solid rgb(203, 214, 226)",
          borderRight: "1px solid rgb(203, 214, 226)",
        }}
      >
        <div
          className={`${messages.length >= 1 || isLoading ? "grow" : "align-center"} flex flex-col relative`}
          style={{}}
        >
          {messages.length === 0 && (
            <div
              className={`mt-3 font-urbanist text-primary-blue text-xl font-light space-y-2 text-center  ${messages.length >= 1 || isLoading ? "" : "mb-20"}`}
            >
              <div>
                <img
                  src={logo}
                  alt="Logo"
                  style={{
                    height: 40,
                    position: "absolute",
                    top: -50,
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                />
              </div>
              <p>👋 Welcome!</p>
              <p>
                I am powered by AI and I am here to answer any questions you
                have about your data.
              </p>
            </div>
          )}
          <ChatMessages messages={messages} isLoading={isLoading} />
          <div style={{ height: 50, width: "100%" }}></div>
          <div>
            <div>
              {messages.length == 0 && (
                <div className="flex gap-4 flex-wrap justify-center">
                  {CHAT_SUGGESTIONS.map((suggestion) => (
                    <ChatSuggestion
                      label={suggestion}
                      onClick={() => setNewMessage(suggestion)}
                    />
                  ))}
                </div>
              )}
              <ChatInput
                newMessage={newMessage}
                isLoading={isLoading}
                setNewMessage={setNewMessage}
                submitNewMessage={submitNewMessage}
              />
            </div>
          </div>

          <div ref={scrollRef} />
        </div>
      </div>
      <div className="p-4 overflow-y-auto">
        <ChatCharts queries={queries} isLoading={isLoading} />
      </div>
    </div>
  );
}

export default Chatbot;
